import { Button, tokens } from "@blurb/codex-react";
import { createStyles } from "@mantine/core";
import { GraphQL } from "@rpi/openapi-api";
import { Flex, RpiSimpleDivider } from "@rpi/openapi-core";
import React from "react";
import { Body, Label } from "../../../components/Typography";
import { TAX_DENIED_MESSAGE, TAX_EXEMPTION_LABEL, UPLOAD_TAX_EXEMPT_DOCUMENT_LABEL } from "./accountSettingsCopy";
import { FieldFrame } from "./FieldFrame";
import { TaxDocumentRow, TaxExemptionDocument } from "./TaxDocumentRow";

const useStyles = createStyles({
  subtle: { color: tokens.color.semantic.text.subtle },
});

interface TaxExemptionSectionProps {
  documents: TaxExemptionDocument[];
  reviewStatus?: GraphQL.TaxReviewStatus | null;
  onUpload: () => void;
}

function finishedDocuments(documents: TaxExemptionDocument[]): TaxExemptionDocument[] {
  return documents.filter((taxDocument) => taxDocument.status === GraphQL.UploadedFileStatus.Finished);
}

/** Newest first; id breaks ties within a batch uploaded at the same instant. */
function byMostRecent(a: TaxExemptionDocument, b: TaxExemptionDocument): number {
  const byCreatedAt = Date.parse(b.createdAt) - Date.parse(a.createdAt);
  return Number.isNaN(byCreatedAt) || byCreatedAt === 0 ? b.id - a.id : byCreatedAt;
}

// Design ruling: only the most recent document is surfaced, however many the customer has uploaded —
// it is the one the current review covers.
function mostRecentDocument(documents: TaxExemptionDocument[]): TaxExemptionDocument | undefined {
  return finishedDocuments(documents).sort(byMostRecent)[0];
}

function isDenied(reviewStatus?: GraphQL.TaxReviewStatus | null): boolean {
  return reviewStatus === GraphQL.TaxReviewStatus.Denied;
}

// Design ruling (Leimmi, Figma thread): the upload action is available in every state — a customer may
// replace their document at any time, and the newest upload supersedes the rest in the view.
export const TaxExemptionSection: React.FC<TaxExemptionSectionProps> = ({ documents, reviewStatus, onUpload }) => {
  const { classes } = useStyles();
  const latestDocument = mostRecentDocument(documents);

  return (
    <Flex.Column gap={8} data-testid="order-tax-exemption">
      <Flex.Row sx={{ color: tokens.color.semantic.text.subtle }}>
        <Label>{TAX_EXEMPTION_LABEL}</Label>
      </Flex.Row>
      <Flex.Row>
        <Button variant="outlined" intent="primary" size="small" onClick={onUpload}>
          {UPLOAD_TAX_EXEMPT_DOCUMENT_LABEL}
        </Button>
      </Flex.Row>
      {latestDocument && (
        <FieldFrame gap={16} data-testid="tax-exemption-documents">
          <TaxDocumentRow taxDocument={latestDocument} reviewStatus={reviewStatus} />
          {isDenied(reviewStatus) && (
            <>
              <RpiSimpleDivider margin={8} />
              <Body className={classes.subtle} data-testid="tax-exemption-denied-message">
                {TAX_DENIED_MESSAGE}
              </Body>
            </>
          )}
        </FieldFrame>
      )}
    </Flex.Column>
  );
};
